import { useMemo, useCallback } from 'react';

// Key generators
const createDeptKey = (orgId, deptId) => `${orgId}::${deptId}`;
const createUserKey = (orgId, deptId, userId) =>
  `${orgId}::${deptId}::${userId}`;

/**
 * Builds lookup maps from nested organizations data
 */
const buildMaps = (organizationsData) => {
  const orgsMap = new Map();
  const deptsMap = new Map();
  const usersMap = new Map();
  const deptsByOrg = new Map();
  const usersByDept = new Map();

  for (const org of organizationsData) {
    if (!org?.id) continue;

    const orgData = { id: org.id, name: org.name };
    orgsMap.set(org.id, orgData);
    deptsByOrg.set(org.id, []);

    for (const dept of org.departments || org.Departments || []) {
      if (!dept?.id) continue;

      const deptKey = createDeptKey(org.id, dept.id);
      const deptData = {
        id: deptKey,
        originalId: dept.id,
        name: dept.name,
        organization: orgData,
      };
      deptsMap.set(deptKey, deptData);
      deptsByOrg.get(org.id).push(deptData);
      usersByDept.set(deptKey, []);

      for (const { user } of dept.departmentUsers || []) {
        if (!user?.id) continue;

        const userKey = createUserKey(org.id, dept.id, user.id);
        const userData = {
          id: userKey,
          originalId: user.id,
          username: user.username,
          department: { id: deptKey, originalId: dept.id, name: dept.name },
          organization: orgData,
        };
        usersMap.set(userKey, userData);
        usersByDept.get(deptKey).push(userData);
      }
    }
  }

  return { orgsMap, deptsMap, usersMap, deptsByOrg, usersByDept };
};

/**
 * Filters user IDs to only those belonging to valid departments
 */
const filterValidUserIds = (userIds, validDeptIds, usersMap) => {
  const deptIdSet = new Set(validDeptIds);
  return userIds.filter((userId) => {
    const user = usersMap.get(userId);
    return user && deptIdSet.has(user.department.id);
  });
};

const formatData = {
  department: (dept) => ({
    id: dept.originalId,
    name: dept.name,
    organization: dept.organization,
  }),
  user: (user) => ({
    id: user.originalId,
    username: user.username,
    department: { id: user.department.originalId, name: user.department.name },
    organization: user.organization,
  }),
};

/**
 * Custom hook for hierarchical multi-select: Organization → Department → User
 *
 * Uses composite keys as IDs for departments/users to ensure uniqueness
 * and enable O(1) lookups.
 *
 * @param {Array} organizationsData - Nested array of organizations
 * @param {Array} organizationIds - Selected organization IDs from Formik
 * @param {Array} departmentIds - Selected department IDs (composite keys) from Formik
 * @param {Array} userIds - Selected user IDs (composite keys) from Formik
 * @param {Function} setFieldValue - Formik setFieldValue function
 */
const useOrganizationHierarchicalSelect = (
  organizationsData = [],
  organizationIds = [],
  departmentIds = [],
  userIds = [],
  setFieldValue = () => {}
) => {
  // Memoize selections to prevent recalculation when arrays have same content
  const selectedOrgIds = useMemo(() => organizationIds, [organizationIds]);
  const selectedDeptIds = useMemo(() => departmentIds, [departmentIds]);
  const selectedUserIds = useMemo(() => userIds, [userIds]);

  // Build all lookup maps
  const { orgsMap, deptsMap, usersMap, deptsByOrg, usersByDept } = useMemo(
    () => buildMaps(organizationsData),
    [organizationsData]
  );

  // Available options for dropdowns
  const availableOrganizations = useMemo(
    () => Array.from(orgsMap.values()),
    [orgsMap]
  );

  const availableDepartments = useMemo(
    () => selectedOrgIds.flatMap((orgId) => deptsByOrg.get(orgId) || []),
    [selectedOrgIds, deptsByOrg]
  );

  const availableUsers = useMemo(
    () => selectedDeptIds.flatMap((deptId) => usersByDept.get(deptId) || []),
    [selectedDeptIds, usersByDept]
  );

  // Selection handlers with cascade
  const handleOrgsChange = useCallback(
    (orgIds) => {
      const orgIdSet = new Set(orgIds);
      setFieldValue('organizationIds', orgIds);

      // Cascade: keep only depts under selected orgs
      const validDeptIds = selectedDeptIds.filter((deptId) => {
        const dept = deptsMap.get(deptId);
        return dept && orgIdSet.has(dept.organization.id);
      });
      setFieldValue('departmentIds', validDeptIds);

      // Cascade: keep only users under valid depts
      setFieldValue(
        'userIds',
        filterValidUserIds(selectedUserIds, validDeptIds, usersMap)
      );
    },
    [setFieldValue, selectedDeptIds, selectedUserIds, deptsMap, usersMap]
  );

  const handleDeptsChange = useCallback(
    (deptIds) => {
      setFieldValue('departmentIds', deptIds);
      setFieldValue(
        'userIds',
        filterValidUserIds(selectedUserIds, deptIds, usersMap)
      );
    },
    [setFieldValue, selectedUserIds, usersMap]
  );

  const handleUsersChange = useCallback(
    (newUserIds) => setFieldValue('userIds', newUserIds),
    [setFieldValue]
  );

  // Full selection with original IDs for backend
  const fullSelection = useMemo(
    () => ({
      organizations: selectedOrgIds
        .map((id) => orgsMap.get(id))
        .filter(Boolean),
      departments: selectedDeptIds
        .map((key) => deptsMap.get(key))
        .filter(Boolean)
        .map(formatData.department),
      users: selectedUserIds
        .map((key) => usersMap.get(key))
        .filter(Boolean)
        .map(formatData.user),
    }),
    [
      selectedOrgIds,
      selectedDeptIds,
      selectedUserIds,
      orgsMap,
      deptsMap,
      usersMap,
    ]
  );

  return {
    availableOrganizations,
    availableDepartments,
    availableUsers,
    selectedOrgIds,
    selectedDeptIds,
    selectedUserIds,
    handleOrgsChange,
    handleDeptsChange,
    handleUsersChange,
    fullSelection,
  };
};

export default useOrganizationHierarchicalSelect;
